import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "calendar" }
const _hoisted_2 = { class: "calendar__title" }
const _hoisted_3 = { class: "calendar__select-page-and-filter-button" }
const _hoisted_4 = { class: "calendar__select-container-group" }
const _hoisted_5 = { class: "calendar__select-container calendar__select-container--warehouse" }
const _hoisted_6 = { class: "calendar__select-container calendar__select-container--supplier" }
const _hoisted_7 = { class: "calendar__info-container" }
const _hoisted_8 = { class: "calendar__info-month-selector" }
const _hoisted_9 = { class: "calendar__info-month-selector-text" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "calendar__info-delivery-date" }
const _hoisted_13 = { class: "calendar__info-delivery-date-confirm" }
const _hoisted_14 = { class: "calendar__info-delivery-date-pending" }
const _hoisted_15 = { class: "calendar__info-delivery-date-text" }
const _hoisted_16 = { class: "calendar__main" }
const _hoisted_17 = { class: "calendar__main-header" }
const _hoisted_18 = { class: "calendar__main-body" }

import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import CalendarDay from '../components/CalendarDay.vue';
import OrderPageTab from '@/corePagesModule/components/OrderPageTab.vue';
import NumberInput from '../components/NumberInput.vue';
import BulkOrderSelectInput from '../components/BulkOrderSelectInput.vue';
import { useOrderCalendarStore } from './orderCalendarStore';
import { storeToRefs } from 'pinia';
import { DateService } from '@/coreDomainsModule/services/dateService';
import SvgLoader from '@/utils/SvgLoader.vue';
import DayModal from '../components/DayModal.vue';
import { CalendarValidationService } from '../domains/services/CalendarValidationService';
import { onBeforeRouteLeave } from 'vue-router';
import { i18n } from '@/i18n';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import MultipleSelectInput from '@/corePagesModule/SelectInput/MultipleSelectInput.vue';
import GroupListMultipleSelectInput from '@/corePagesModule/group/components/GroupListMultipleSelectInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const t = i18n.t;

const orderRepository = new OrderRepository();
const dayNames = [t('曜日:日'), t('曜日:月'), t('曜日:火'), t('曜日:水'), t('曜日:木'), t('曜日:金'), t('曜日:土')];
const orderCalendarStore = useOrderCalendarStore();
const { state } = storeToRefs(orderCalendarStore);
const { fetchOrders, getOrdersByDate, onSelectedGroup, onAppliedFilterModal, getFilterItemsValue } = orderCalendarStore;

const isOpenSaveSuccessModal = ref(false);
const isOpenSaveErrorModal = ref(false);

onMounted(async () => {
  await fetchOrders(orderRepository);
});

onUnmounted(() => {});

onBeforeRouteLeave((to, from, next) => {
  orderCalendarStore.$reset();
  orderCalendarStore.$dispose();
  next(); // 遷移を続行
});

/**
 * 指定された日付が属する月のカレンダーデータを生成する。
 * @param date - 特定の月を表すDateオブジェクト。
 * @returns 特定の月のカレンダーデータを含む週の配列。各日付は当月のものか、週末かどうかの情報を含む。
 */
const getMonthData = (date: Date) => {
  const result: Array<Array<{ date: Date; isWeekend: boolean; isCurrentMonth: boolean }>> = [];
  const month = date.getMonth();
  const year = date.getFullYear();

  // 月の最初の日
  const firstDayOfMonth = new Date(year, month, 1);
  // 最初の日が属する週の日曜日
  const startDay = new Date(firstDayOfMonth);
  startDay.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

  // 月の最後の日
  const lastDayOfMonth = new Date(year, month + 1, 0);
  // 最後の日が属する週の土曜日（6週間後の土曜日に修正）
  const endDay = new Date(lastDayOfMonth);
  const daysToAdd = 5 * 7 - lastDayOfMonth.getDay();
  endDay.setDate(lastDayOfMonth.getDate() + daysToAdd);

  let dateCursor = new Date(startDay);
  while (dateCursor <= endDay) {
    const week: Array<{ date: Date; isWeekend: boolean; isCurrentMonth: boolean }> = [];
    for (let day = 0; day < 7; day++) {
      week.push({
        date: new Date(dateCursor),
        isWeekend: dateCursor.getDay() === 0 || dateCursor.getDay() === 6,
        isCurrentMonth: dateCursor.getMonth() === month,
      });
      dateCursor.setDate(dateCursor.getDate() + 1);
    }
    result.push(week);
  }

  return result;
};

/**
 * 前の月に移動する関数
 */
const goToPreviousMonth = () => {
  const newDate = DateService.getShiftedDateByMonths(state.value.currentMonthDate, -1);
  state.value.currentMonthDate = newDate; // 新しいDateインスタンスを代入
  fetchOrders(orderRepository);
};

/**
 * 次の月に移動する関数
 */
const goToNextMonth = () => {
  const newDate = DateService.getShiftedDateByMonths(state.value.currentMonthDate, 1);
  state.value.currentMonthDate = newDate; // 新しいDateインスタンスを代入
  fetchOrders(orderRepository);
};

/**
 * 日付モーダルの保存後の処理
 * @param isSuccess - 保存が成功したかどうか
 */
const onDayModalSaved = async (isSuccess: boolean) => {
  await waitLoading(fetchOrders(orderRepository));
  if (isSuccess) {
    // 成功した旨のモーダルを開く
    isOpenSaveSuccessModal.value = true;
  } else {
    // 失敗した旨のモーダルを開く
    isOpenSaveErrorModal.value = true;
  }
};

/**
 * 保存成功モーダルを閉じる
 */
const closeSaveSuccessModal = () => {
  isOpenSaveSuccessModal.value = false;
};

/**
 * 保存失敗モーダルを閉じる
 */
const closeSaveErrorModal = () => {
  isOpenSaveErrorModal.value = false;
};

const weeks = computed(() => getMonthData(state.value.currentMonthDate));
const currentMonth = computed(() => state.value.currentMonthDate.getMonth());
const currentYear = computed(() => state.value.currentMonthDate.getFullYear());
const unit = computed(() => state.value.allOrders[0]?.unit ?? '');

// watch(
//   () => state.value.selectedInfo.warehouseName,
//   async () => {
//     await simulateInventory(organizationBusinessRepository, { year: currentYear.value, month: currentMonth.value + 1 });
//   }
// );

// watch(
//   () => currentMonth.value,
//   async () => {
//     await simulateInventory(organizationBusinessRepository, { year: currentYear.value, month: currentMonth.value + 1 });
//   }
// );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_unref(t)('発注')), 1),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(OrderPageTab, { class: "" }),
      _createVNode(FilterModalButton, {
        class: "detail-filter-modal",
        "filter-modal-type": 'order-calendar',
        "select-input-index": 0,
        "filter-items": _unref(getFilterItemsValue)().value,
        onApply: _cache[0] || (_cache[0] = (filteredContents) => _unref(onAppliedFilterModal)(_unref(orderRepository), filteredContents))
      }, null, 8, ["filter-items"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(MultipleSelectInput, {
          index: 20,
          "selected-list": _unref(state).selectedInfo.warehouseName,
          placeholder: _unref(t)('置場名'),
          label: _unref(t)('置場名'),
          "filter-type": 'WAREHOUSE',
          "is-open-prop": _unref(state).filterInfo.isOpenFilterType === 'WAREHOUSE',
          "use-teleport": "",
          "onClick:selectInput": _cache[1] || (_cache[1] = () => (_unref(state).filterInfo.isOpenFilterType = 'WAREHOUSE')),
          "onUpdate:modelValue": _cache[2] || (_cache[2] = (e) => (_unref(state).selectedInfo.warehouseName = e)),
          onClose: _cache[3] || (_cache[3] = ($event: any) => (_unref(fetchOrders)(_unref(orderRepository))))
        }, null, 8, ["selected-list", "placeholder", "label", "is-open-prop"]),
        _createVNode(NumberInput, {
          "model-value": _unref(state).selectedInfo.dailyDeliveryLimit,
          index: 21,
          placeholder: _unref(t)('1日の納入上限数量'),
          label: _unref(t)('1日の納入上限数量'),
          disabled: _unref(state).selectedInfo.warehouseName.length === 0,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = (e) => (_unref(state).selectedInfo.dailyDeliveryLimit = e))
        }, null, 8, ["model-value", "placeholder", "label", "disabled"])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(GroupListMultipleSelectInput, {
          "select-input-index": 22,
          "store-tag": 'orderCalendar',
          "selected-group-ids-prop": _unref(state).selectedInfo.groupId,
          "show-label": true,
          "multi-selectable": true,
          "use-teleport": "",
          onSelectedGroups: _cache[5] || (_cache[5] = (groupIds) => _unref(onSelectedGroup)(_unref(orderRepository), groupIds))
        }, null, 8, ["selected-group-ids-prop"]),
        _createVNode(BulkOrderSelectInput, {
          "model-value": _unref(state).selectedInfo.bulkOrder,
          index: 23,
          placeholder: _unref(t)('まとめ発注'),
          disabled: _unref(state).selectedInfo.groupId.length === 0,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = (e) => (_unref(state).selectedInfo.bulkOrder = e))
        }, null, 8, ["model-value", "placeholder", "disabled"])
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('発注カレンダー(表示年月切り替え)', { year: currentYear.value, month: currentMonth.value + 1 })), 1),
        _createElementVNode("button", {
          disabled: !_unref(CalendarValidationService).isCanMovePreviousMonth(new Date(currentYear.value, currentMonth.value)),
          class: "calendar__info-month-selector-button",
          onClick: goToPreviousMonth
        }, [
          _createVNode(SvgLoader, { name: "arrowLeft" })
        ], 8, _hoisted_10),
        _createElementVNode("button", {
          disabled: !_unref(CalendarValidationService).isCanMoveNextMonth(new Date(currentYear.value, currentMonth.value)),
          class: "calendar__info-month-selector-button",
          onClick: goToNextMonth
        }, [
          _createVNode(SvgLoader, { name: "arrowRight" })
        ], 8, _hoisted_11)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, _toDisplayString(_unref(t)('納期確定')), 1),
        _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(t)('納期未確定')), 1),
        _createElementVNode("div", _hoisted_15, _toDisplayString(_unref(t)('納期が未確定の発注はドラッグ&ドロップで移動可能です。')), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_16, [
      _createElementVNode("div", _hoisted_17, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(dayNames, (dayName) => {
          return _createElementVNode("div", {
            key: dayName,
            class: _normalizeClass(`calendar__main-header-text ${
            [_unref(t)('曜日:土'), _unref(t)('曜日:日')].includes(dayName) ? 'calendar__main-header-text--weekend' : ''
          }`)
          }, _toDisplayString(_unref(t)(dayName)), 3)
        }), 64))
      ]),
      _createElementVNode("div", _hoisted_18, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weeks.value.slice(0, 6), (week) => {
          return (_openBlock(), _createElementBlock("div", {
            key: week[0].date.toDateString(),
            class: "calendar__main-body-week"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(week, (day) => {
              return (_openBlock(), _createBlock(CalendarDay, {
                key: day.date.toDateString(),
                date: _unref(DateService).convertDateToIDate(day.date),
                "is-weekend": day.isWeekend || _unref(DateService).isHoliday(day.date),
                orders: _unref(getOrdersByDate)(_unref(DateService).convertDateToIDate(day.date))
              }, null, 8, ["date", "is-weekend", "orders"]))
            }), 128))
          ]))
        }), 128))
      ])
    ]),
    _createVNode(DayModal, { onOnSaved: onDayModalSaved }),
    _createVNode(InfoModal, {
      "modal-state": isOpenSaveSuccessModal.value,
      icon: "info",
      "back-button-label": _unref(t)('閉じる'),
      onOnBack: closeSaveSuccessModal,
      onOutsideClick: closeSaveSuccessModal
    }, {
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)('変更に成功しました。')), 1)
      ]),
      _: 1
    }, 8, ["modal-state", "back-button-label"]),
    _createVNode(InfoModal, {
      "modal-state": isOpenSaveErrorModal.value,
      icon: "error",
      "color-style": "error",
      "back-button-label": _unref(t)('確認'),
      onOnBack: closeSaveErrorModal,
      onOutsideClick: closeSaveErrorModal
    }, {
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)('登録に失敗しました。')), 1),
        _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1)),
        _createTextVNode(_toDisplayString(_unref(t)('時間をおいて再度登録をお願いします。')), 1)
      ]),
      _: 1
    }, 8, ["modal-state", "back-button-label"])
  ]))
}
}

})